'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { type LucideIcon } from 'lucide-react'

/* -------------------------------------------------
 * Types
 * ------------------------------------------------- */
export interface Step {
  icon: LucideIcon
  title: string
  description: string
}

interface WhatToExpectSectionProps {
  steps: Step[]
  mainTitle?: string
  mainDescription?: string
}

/* -------------------------------------------------
 * Reusable card for each timeline step
 * ------------------------------------------------- */
interface StepCardProps extends Step {
  align?: 'left' | 'right'
}

const StepCard: React.FC<StepCardProps> = ({
  icon: Icon,
  title,
  description,
  align = 'left',
}) => (
  <div
    className={`bg-white p-5 sm:p-6 rounded-xl shadow-md ring-1 ring-border transition-transform will-change-transform hover:-translate-y-1 duration-300 ${
      align === 'right' ? 'text-right' : ''
    }`}
  >
    <div
      className={`flex items-center mb-3 sm:mb-4 ${
        align === 'right' ? 'justify-end' : ''
      }`}
    >
      {align === 'right' ? (
        <>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mr-3">
            {title}
          </h3>
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
          </div>
        </>
      ) : (
        <>
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            {title}
          </h3>
        </>
      )}
    </div>
    <p className="text-sm sm:text-base text-gray-700">{description}</p>
  </div>
)

/* -------------------------------------------------
 * Animation presets
 * ------------------------------------------------- */
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
}

/* -------------------------------------------------
 * Main section component
 * ------------------------------------------------- */
const WhatToExpectSection: React.FC<WhatToExpectSectionProps> = ({
  steps,
  mainTitle = 'Your Evaluation Journey: What to Expect at ReACH',
  mainDescription = "We strive to make your evaluation process as comfortable and clear as possible. Here's what you can typically expect:",
}) => {
  // An empty steps list must not render a full padded section with a bare
  // heading and nothing beneath it.
  if (!steps?.length) return null
  return (
    <section id="what-to-expect" className="py-12 sm:py-16 md:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-10 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeIn}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4">
            {mainTitle}
          </h2>
          <p className="text-base sm:text-lg text-gray-700 max-w-3xl mx-auto">
            {mainDescription}
          </p>
        </motion.div>

        {/* ========== MOBILE TIMELINE (< md) ========== */}
        <motion.div
          className="md:hidden relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={staggerContainer}
        >
          <div className="space-y-6">
            {steps.map((step, idx) => {
              const isOdd = idx % 2 !== 0
              return (
                <motion.div
                  key={step.title + idx}
                  className={`relative flex items-start gap-3 ${isOdd ? 'flex-row-reverse' : ''}`}
                  variants={fadeIn}
                >
                  {/* Numbered circle */}
                  <div className="relative z-10 flex-shrink-0 w-10 h-10 bg-[#d0ff71] rounded-full flex items-center justify-center text-black font-bold text-sm shadow-sm mt-4">
                    {idx + 1}
                  </div>
                  {/* Card */}
                  <div className="flex-1">
                    <StepCard {...step} align={isOdd ? 'right' : 'left'} />
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* ========== DESKTOP TIMELINE (>= md) ========== */}
        <motion.div
          className="hidden md:grid md:grid-cols-5 gap-6 relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          {/* Vertical center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#5fbce2] transform -translate-x-1/2 z-0" />

          {steps.map((step, idx) => {
            const isEven = idx % 2 === 0
            return (
              <React.Fragment key={step.title + idx}>
                {/* Left block */}
                {isEven ? (
                  <motion.div
                    className="md:col-span-2 md:text-right"
                    variants={fadeIn}
                  >
                    <StepCard {...step} align="right" />
                  </motion.div>
                ) : (
                  <div className="md:col-span-2" />
                )}

                {/* Mid circle */}
                <motion.div
                  className="flex items-center justify-center"
                  variants={fadeIn}
                >
                  <div className="w-10 h-10 bg-[#d0ff71] rounded-full flex items-center justify-center text-black font-bold relative z-10">
                    {idx + 1}
                  </div>
                </motion.div>

                {/* Right block */}
                {isEven ? (
                  <div className="md:col-span-2" />
                ) : (
                  <motion.div className="md:col-span-2" variants={fadeIn}>
                    <StepCard {...step} />
                  </motion.div>
                )}
              </React.Fragment>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}

export default WhatToExpectSection
